/**
 * Bolha de mensagem - Versão Premium
 * Com animações de entrada, status visuais, avatar de bot e botões interativos
 * Suporta mensagens do Flow Engine com botões nativos
 * Inclui lightbox para preview de imagens e vídeos em tela cheia
 */

import React, { useState, useMemo } from 'react';

/**
 * Bolha de mensagem - Versão Premium
 * Com animações de entrada, status visuais, avatar de bot e botões interativos
 * Suporta mensagens do Flow Engine com botões nativos
 * Inclui lightbox para preview de imagens e vídeos em tela cheia
 * Suporta mensagens de sistema (assumir/encerrar conversa) com estilo diferenciado
 */

import type { ChatMessage, InteractiveButton } from '../types';
import { MediaLightbox } from './MediaLightbox';
import { getStorageUrl } from '../utils/storageUrl';
import { createLogger } from '@/lib/logger';
import { normalizeEmbedUrl } from '@/lib/embed-utils';

// Logger para diagnóstico
const logger = createLogger('MessageBubble');

interface MessageBubbleProps {
  message: ChatMessage;
  primaryColor: string;
  onRetry?: () => void;
  onButtonClick?: (button: InteractiveButton) => void;
  isLast?: boolean;
}

// Ícone de robô para avatar do bot (SVG inline)
const BotIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" />
    <line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);

// ============= ÍCONES PARA BOTÕES PREMIUM =============
// Ícones SVG inline para botões interativos premium
const ButtonIcons: Record<string, React.FC<{ color: string }>> = {
  message: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  headphones: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
      <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  ),
  help: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  document: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  cart: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  ),
  user: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  dollar: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  default: ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

// Ícone de seta (chevron) para botões premium
const ChevronRightIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * Componente de avatar inteligente - exibe foto do agente ou ícone de bot
 * Para mensagens de agentes com avatar, exibe a imagem real
 * Fallback para ícone de bot padrão
 */
const SenderAvatar: React.FC<{
  senderType: string;
  senderName?: string;
  senderAvatar?: string;
  primaryColor: string;
}> = ({ senderType, senderName, senderAvatar, primaryColor }) => {
  const [imageError, setImageError] = useState(false);
  
  // Estilos do avatar (mesmo do avatarStyles original)
  const avatarStyles: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  };
  
  // Se tem avatar de agente válido (sem erro de carregamento)
  if (senderAvatar && senderType === 'agent' && !imageError) {
    logger.debug('Renderizando avatar do agente', { senderName, senderAvatar: senderAvatar.substring(0, 50) });
    
    return (
      <div style={avatarStyles}>
        <img
          src={senderAvatar}
          alt={senderName || 'Agente'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => {
            logger.warn('Erro ao carregar avatar do agente, usando fallback', { senderAvatar });
            setImageError(true);
          }}
        />
      </div>
    );
  }
  
  // Fallback: ícone de bot padrão
  return (
    <div style={avatarStyles}>
      <BotIcon color={primaryColor} />
    </div>
  );
};

// Ícone de zoom/expandir para overlay de mídias clicáveis
const ZoomIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  primaryColor,
  onRetry,
  onButtonClick,
  isLast = false,
}) => {
  const isInbound = message.direction === 'inbound';
  const isOutbound = !isInbound;
  const isSystemMessage = message.sender_type === 'system';
  
  // Estados para o lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Filtra apenas mídias visualizáveis (imagens e vídeos) para o lightbox
  const mediaItems = useMemo(() => {
    if (!message.attachments) return [];
    return message.attachments
      .filter(att => att.type === 'image' || att.type === 'video')
      .map(att => ({
        url: getStorageUrl(att.url),
        type: att.type as 'image' | 'video',
        filename: att.filename,
      }));
  }, [message.attachments]);

  // Handler para abrir o lightbox ao clicar em uma mídia
  const handleMediaClick = (attachmentIndex: number) => {
    // Encontra o índice real dentro do array mediaItems
    const attachment = message.attachments?.[attachmentIndex];
    if (!attachment || (attachment.type !== 'image' && attachment.type !== 'video')) return;
    
    const mediaIndex = mediaItems.findIndex(m => m.url === attachment.url);
    if (mediaIndex !== -1) {
      setLightboxIndex(mediaIndex);
      setLightboxOpen(true);
    }
  };
  
  // Estilos do container principal com animação
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: isInbound ? 'flex-end' : 'flex-start',
    marginBottom: '12px',
    padding: '0 12px',
    animation: isLast ? 'messageSlideIn 0.3s ease-out' : 'none',
  };

  // Estilos específicos para mensagens de sistema (centralizadas e discretas)
  const systemContainerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '12px',
    padding: '0 12px',
  };

  const systemMessageStyles: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '6px 14px',
    borderRadius: '12px',
    textAlign: 'center',
    maxWidth: '85%',
    lineHeight: '1.4',
  };

  // Estilos do wrapper com avatar (para mensagens outbound/bot)
  const messageWrapperStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    maxWidth: '90%',
  };

  // Estilos da bolha de mensagem
  const bubbleStyles: React.CSSProperties = {
    maxWidth: isOutbound ? '100%' : '85%',
    minWidth: 'fit-content', // Impede que o balão seja menor que o conteúdo
    padding: '10px 14px',
    borderRadius: isInbound ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    backgroundColor: isInbound ? primaryColor : '#f3f4f6',
    color: isInbound ? '#ffffff' : '#1f2937',
    fontSize: '14px',
    lineHeight: '1.5',
    wordBreak: 'normal', // Quebra natural de palavras
    overflowWrap: 'break-word', // Quebra palavras longas apenas quando necessário
    whiteSpace: 'pre-wrap', // Preserva espaços e quebras naturais de linha
    boxShadow: isInbound 
      ? `0 2px 8px ${primaryColor}30`
      : '0 1px 4px rgba(0, 0, 0, 0.06)',
    position: 'relative',
  };

  // Estilos para attachments de mídia (clicáveis)
  const mediaContainerStyles: React.CSSProperties = {
    position: 'relative',
    cursor: 'pointer',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '8px',
  };

  const mediaStyles: React.CSSProperties = {
    maxWidth: '100%',
    borderRadius: '8px',
    display: 'block',
  };

  const mediaOverlayStyles: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    borderRadius: '8px',
  };

  const zoomIconContainerStyles: React.CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    opacity: 0,
    transition: 'opacity 0.2s',
  };

  const videoStyles: React.CSSProperties = {
    ...mediaStyles,
    maxHeight: '200px',
  };

  const audioStyles: React.CSSProperties = {
    width: '100%',
    marginBottom: '8px',
  };

  const documentLinkStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    color: '#374151',
    textDecoration: 'none',
    fontSize: '13px',
    marginBottom: '8px',
  };

  // ============= ESTILOS DOS BOTÕES PREMIUM =============
  // Container dos botões interativos (fora da bolha)
  const buttonContainerStyles: React.CSSProperties = {
    marginTop: '10px',
    marginLeft: isOutbound ? '40px' : '0', // Alinha com o conteúdo (após avatar)
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: 'calc(90% - 40px)',
  };

  // Estilos do card premium de botão
  const premiumButtonStyles: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    cursor: 'pointer',
    textAlign: 'left' as const,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.2s ease',
  };

  // Container do ícone do botão premium
  const iconContainerStyles: React.CSSProperties = {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: `${primaryColor}15`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  // Área de texto do botão premium
  const textAreaStyles: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '15px',
    fontWeight: 400,
    color: '#1f2937',
    margin: 0,
    lineHeight: 1.3,
  };

  const descriptionStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#6b7280',
    margin: '2px 0 0 0',
    lineHeight: 1.4,
  };

  // Seta de navegação do botão premium
  const chevronStyles: React.CSSProperties = {
    flexShrink: 0,
    color: '#9ca3af',
  };

  // Estilos do timestamp e status
  const metaStyles: React.CSSProperties = {
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  // Formata timestamp
  const formatTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  // Ícones de status
  const StatusIcon = () => {
    if (!isInbound) return null;
    const iconProps = { width: 14, height: 14, viewBox: "0 0 24 24", fill: "none", strokeWidth: 2 };
    
    switch (message.status) {
      case 'pending':
        return <svg {...iconProps} stroke="#9ca3af" style={{ animation: 'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" /></svg>;
      case 'sent':
        return <svg {...iconProps} stroke="#9ca3af"><polyline points="20,6 9,17 4,12" /></svg>;
      case 'delivered':
        return <svg {...iconProps} stroke="#9ca3af"><polyline points="18,7 9,17 5,13" /><polyline points="22,7 13,17" /></svg>;
      case 'read':
        return <svg {...iconProps} stroke={primaryColor}><polyline points="18,7 9,17 5,13" /><polyline points="22,7 13,17" /></svg>;
      case 'failed':
        return <svg {...iconProps} stroke="#ef4444"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>;
      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes messageSlideIn { 0% { opacity: 0; transform: translateY(10px) scale(0.95); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .uaizychat-retry-btn { background: none; border: none; color: #ef4444; cursor: pointer; font-size: 11px; text-decoration: underline; padding: 0; margin-left: 4px; }
        .uaizychat-premium-btn:hover { background-color: #f9fafb !important; border-color: ${primaryColor} !important; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important; }
        .uaizychat-premium-btn:active { transform: translateY(0); }
        .uaizychat-media-container:hover .uaizychat-media-overlay { background-color: rgba(0, 0, 0, 0.3) !important; }
        .uaizychat-media-container:hover .uaizychat-zoom-icon { opacity: 1 !important; }
      `}</style>
      
      {/* Mensagem de sistema - layout centralizado e discreto */}
      {isSystemMessage ? (
        <div style={systemContainerStyles} role="listitem">
          <span style={systemMessageStyles}>{message.content}</span>
        </div>
      ) : (
      <div style={containerStyles} role="listitem">
        {/* Layout com avatar para mensagens do bot (outbound) */}
        <div style={messageWrapperStyles}>
          {/* Avatar inteligente - bot ou agente com foto */}
          {isOutbound && (
            <SenderAvatar
              senderType={message.sender_type}
              senderName={message.sender_name}
              senderAvatar={message.sender_avatar}
              primaryColor={primaryColor}
            />
          )}
          
          {/* Conteúdo da mensagem */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={bubbleStyles}>
              {/* Renderiza attachments antes do conteúdo de texto */}
              {message.attachments && message.attachments.length > 0 && (
                <div style={{ marginBottom: message.content ? '8px' : '0' }}>
                  {message.attachments.map((att, idx) => (
                    <div key={idx}>
                      {att.type === 'image' && (
                        <div 
                          style={mediaContainerStyles}
                          className="uaizychat-media-container"
                          onClick={() => handleMediaClick(idx)}
                          role="button"
                          tabIndex={0}
                          aria-label={`Visualizar imagem: ${att.filename || 'Imagem'}`}
                          onKeyDown={(e) => e.key === 'Enter' && handleMediaClick(idx)}
                        >
                          <img 
                            src={getStorageUrl(att.url)} 
                            alt={att.filename || 'Imagem'} 
                            style={mediaStyles}
                            loading="lazy"
                          />
                          <div style={mediaOverlayStyles} className="uaizychat-media-overlay">
                            <div style={zoomIconContainerStyles} className="uaizychat-zoom-icon">
                              <ZoomIcon />
                            </div>
                          </div>
                        </div>
                      )}
                      {att.type === 'video' && (
                        <div 
                          style={mediaContainerStyles}
                          className="uaizychat-media-container"
                          onClick={() => handleMediaClick(idx)}
                          role="button"
                          tabIndex={0}
                          aria-label={`Visualizar vídeo: ${att.filename || 'Vídeo'}`}
                          onKeyDown={(e) => e.key === 'Enter' && handleMediaClick(idx)}
                        >
                          <video 
                            src={getStorageUrl(att.url)} 
                            style={videoStyles}
                            preload="metadata"
                          />
                          <div style={mediaOverlayStyles} className="uaizychat-media-overlay">
                            <div style={zoomIconContainerStyles} className="uaizychat-zoom-icon">
                              <ZoomIcon />
                            </div>
                          </div>
                        </div>
                      )}
                      {att.type === 'audio' && (
                        <audio 
                          src={getStorageUrl(att.url)} 
                          controls 
                          style={audioStyles}
                          preload="metadata"
                        />
                      )}
                      {att.type === 'document' && (
                        <a 
                          href={getStorageUrl(att.url)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={documentLinkStyles}
                        >
                          📎 {att.filename || 'Documento'}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Conteúdo incorporado (iframe) do Flow Engine */}
              {message.metadata?.embed_url && (
                <div style={{ marginBottom: message.content ? '8px' : '0', borderRadius: '8px', overflow: 'hidden' }}>
                  <iframe
                    src={normalizeEmbedUrl(message.metadata.embed_url as string)}
                    width="100%"
                    height={Number(message.metadata.embed_height) || 400}
                    style={{ border: 'none', display: 'block' }}
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Conteúdo incorporado"
                  />
                </div>
              )}

              {message.content}
              
              {/* Indicador de erro */}
              {message.status === 'failed' && (
                <div style={{ position: 'absolute', top: '-8px', right: '-8px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </div>
              )}
            </div>
            
            {/* Timestamp e status */}
            <div style={metaStyles}>
              <span>{formatTime(message.sent_at)}</span>
              <StatusIcon />
              {message.status === 'failed' && onRetry && <button onClick={onRetry} className="uaizychat-retry-btn" aria-label="Reenviar mensagem">Reenviar</button>}
            </div>
          </div>
        </div>
        
        {/* Botões interativos do Flow Engine - LAYOUT PREMIUM */}
        {message.buttons && message.buttons.length > 0 && (
          <div style={buttonContainerStyles}>
            {message.buttons.map((button) => {
              // Seleciona o ícone baseado no campo icon do botão (fallback para default)
              const IconComponent = ButtonIcons[button.icon || 'default'] || ButtonIcons.default;
              
              return (
                <button
                  key={button.id}
                  className="uaizychat-premium-btn"
                  onClick={() => onButtonClick?.(button)}
                  style={premiumButtonStyles}
                >
                  {/* Container do ícone */}
                  <div style={iconContainerStyles}>
                    <IconComponent color={primaryColor} />
                  </div>
                  
                  {/* Área de texto (título + descrição opcional) */}
                  <div style={textAreaStyles}>
                    <p style={titleStyles}>{button.text}</p>
                    {button.description && (
                      <p style={descriptionStyles}>{button.description}</p>
                    )}
                  </div>
                  
                  {/* Seta de navegação */}
                  <div style={chevronStyles}>
                    <ChevronRightIcon />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Lightbox para preview de mídia em tela cheia */}
      <MediaLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={mediaItems}
        initialIndex={lightboxIndex}
        primaryColor={primaryColor}
      />
    </>
  );
};
